
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import Card from '../../components/base/Card';

export default function Home() {
  const products = [
    {
      name: 'Washer Extractors',
      description: '15-200kg capacity, front/side-loading with high G-force extraction',
      image: '/images/home/washer-extractor.jpg',
    },
    {
      name: 'Tumble Dryers',
      description: 'Gas/electric/steam options with advanced moisture sensing technology',
      image: '/images/home/tumble-dryer.jpg',
    },
    {
      name: 'Flat Work Ironers',
      description: 'Industrial pressing systems with multi-roll and temperature control',
      image: '/images/home/flat-work-ironer.jpg',
    },
    {
      name: 'Barrier Washers',
      description: 'Hygienic isolation washing for medical and contaminated textiles',
      image: '/images/home/barrier-washer.jpg',
    },
    {
      name: 'Folding Machines',
      description: 'Automated folding systems for towels, sheets and uniform processing',
      image: '/images/home/folding-machine.jpg',
    },
    {
      name: 'Steam Pressing Units',
      description: 'Professional garment finishing with steam and vacuum technology',
      image: '/images/home/steam-press.jpg',
    },
  ];

  const industries = [
    {
      icon: 'ri-hotel-line',
      title: 'Hotels & Resorts',
      description:
        'Complete laundry solutions for hospitality industry with high-volume requirements',
      features: ['24/7 Operation', 'Energy Efficient', 'Guest Satisfaction'],
    },
    {
      icon: 'ri-hospital-line',
      title: 'Hospitals & Healthcare',
      description:
        'Hygienic washing systems meeting medical industry standards and regulations',
      features: ['Medical Grade', 'Infection Control', 'Compliance Ready'],
    },
    {
      icon: 'ri-building-2-line',
      title: 'Laundromats',
      description:
        'Self-service and commercial laundry equipment for retail laundry businesses',
      features: ['User Friendly', 'Coin/Card Ready', 'Reliable Operations'],
    },
    {
      icon: 'ri-factory-line',
      title: 'Industrial Facilities',
      description:
        'Heavy-duty equipment for manufacturing, textile and industrial applications',
      features: ['Heavy Duty', 'High Capacity', 'Continuous Operation'],
    },
  ];

  const testimonials = [
    {
      name: 'Rajesh Malhotra',
      position: 'General Manager',
      company: 'Grand Palace Hotel',
      content:
        'GalaxyFabTech transformed our laundry operations completely. The equipment quality and after-sales support exceeded our expectations.',
      rating: 5,
      image: '/images/home/testimonial-hotel-manager.jpg',
    },
    {
      name: 'Dr. Priya Sharma',
      position: 'Operations Director',
      company: 'Apollo Medical Center',
      content:
        'The medical‑grade laundry equipment has helped us maintain the highest hygiene standards. Excellent technical support team.',
      rating: 5,
      image: '/images/home/testimonial-doctor.jpg',
    },
    {
      name: 'Amit Kumar',
      position: 'Owner',
      company: 'Clean Pro Laundromat',
      content:
        'Best investment for our business. The machines are reliable, efficient, and our customers love the quality of wash.',
      rating: 5,
      image: '/images/home/testimonial-business-owner.jpg',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-teal-50">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 pt-32">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-96 h-96 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-r from-teal-500/20 to-cyan-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>
        </div>

        {/* Industry Visual Elements */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Washing Machine Icons */}
          <div className="absolute top-1/4 left-12 opacity-20">
            <div className="w-16 h-16 bg-white/10 rounded-lg flex items-center justify-center animate-bounce delay-300">
              <i className="ri-shirt-line text-2xl text-white/60"></i>
            </div>
          </div>
          <div className="absolute top-1/3 right-16 opacity-15">
            <div className="w-20 h-20 bg-white/10 rounded-lg flex items-center justify-center animate-bounce delay-700">
              <i className="ri-water-flash-line text-3xl text-white/60"></i>
            </div>
          </div>
          <div className="absolute bottom-1/4 left-1/4 opacity-20">
            <div className="w-14 h-14 bg-white/10 rounded-lg flex items-center justify-center animate-bounce delay-1000">
              <i className="ri-settings-4-line text-xl text-white/60"></i>
            </div>
          </div>

          {/* Equipment Silhouettes */}
          <div className="absolute top-20 right-20 opacity-10">
            <div className="w-24 h-32 bg-white/20 rounded-lg flex flex-col items-center justify-center">
              <div className="w-16 h-16 bg-white/30 rounded-full mb-2"></div>
              <div className="w-12 h-8 bg-white/30 rounded"></div>
            </div>
          </div>
          <div className="absolute bottom-20 left-20 opacity-10">
            <div className="w-20 h-28 bg-white/20 rounded-lg flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-white/30 rounded-full mb-2"></div>
              <div className="w-10 h-6 bg-white/30 rounded"></div>
            </div>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white z-10 py-8">
          {/* Industry Badge */}
          <div className="mb-8">
            <span className="inline-flex items-center px-6 py-3 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-sm font-medium text-white/90">
              <div className="w-8 h-8 bg-blue-500/30 rounded-full flex items-center justify-center mr-3">
                <i className="ri-building-line text-sm text-white"></i>
              </div>
              Find Your Perfect Equipment Specialists
              <span className="w-2 h-2 bg-green-400 rounded-full ml-3 animate-pulse"></span>
            </span>
          </div>

          <h1 className="text-5xl md:text-8xl font-bold mb-10 leading-tight">
            <span className="bg-gradient-to-r from-white via-blue-100 to-teal-200 bg-clip-text text-transparent">
              Complete Laundry
            </span>
            <br />
            <span className="bg-gradient-to-r from-teal-300 via-blue-300 to-purple-300 bg-clip-text text-transparent">
              Solutions
            </span>
          </h1>

          <p className="text-xl md:text-2xl mb-12 max-w-4xl mx-auto leading-relaxed text-white/80">
            <span className="font-semibold text-yellow-300">
              Premium Commercial Equipment
            </span>{' '}
            from trusted global manufacturers
            <br />
            <span className="text-lg text-white/70">
              Serving Hotels • Hospitals • Laundromats • Industries across India
            </span>
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <Link to="/products">
              <button className="group relative px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 rounded-full text-white font-semibold text-base transition-all duration-300 shadow-lg hover:shadow-2xl hover:shadow-blue-500/25 transform hover:-translate-y-1 whitespace-nowrap">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-blue-500 rounded-full blur opacity-0 group-hover:opacity-50 transition-opacity duration-300"></div>
                <span className="relative flex items-center">
                  <i className="ri-shopping-cart-line mr-2"></i>
                  Explore Equipment
                </span>
              </button>
            </Link>

            <Link to="/services">
              <button className="group relative px-6 py-3 bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 rounded-full text-white font-semibold text-base transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:-translate-y-1 whitespace-nowrap">
                <span className="relative flex items-center">
                  <i className="ri-service-line mr-2"></i>
                  Our Services
                </span>
              </button>
            </Link>

            <Link to="/contact">
              <button className="group relative px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 rounded-full text-white font-semibold text-base transition-all duration-300 shadow-lg hover:shadow-2xl hover:shadow-teal-500/25 transform hover:-translate-y-1 whitespace-nowrap">
                <div className="absolute inset-0 bg-gradient-to-r from-teal-400 to-teal-500 rounded-full blur opacity-0 group-hover:opacity-50 transition-opacity duration-300"></div>
                <span className="relative flex items-center">
                  <i className="ri-phone-line mr-2"></i>
                  Get Consultation
                </span>
              </button>
            </Link>
          </div>

          <div className="flex items-center justify-center text-white/60">
            <div className="flex items-center">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                ))}
              </div>
              <span className="ml-2 text-sm">4.9/5 Customer Rating</span>
            </div>
          </div>

          {/* Floating Elements */}
          <div className="absolute top-1/4 left-1/4 w-4 h-4 bg-blue-400 rounded-full animate-bounce delay-300"></div>
          <div className="absolute top-1/3 right-1/4 w-6 h-6 bg-teal-400 rounded-full animate-bounce delay-700"></div>
          <div className="absolute bottom-1/4 left-1/3 w-3 h-3 bg-purple-400 rounded-full animate-bounce delay-1000"></div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-teal-50">
        {/* Beautiful Background */}
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
            style={{
              backgroundImage: `url('/images/backgrounds/featured-products-bg.jpg')`,
            }}
          />

          {/* Floating Elements */}
          <div className="absolute top-20 left-20 w-32 h-32 bg-blue-200/30 rounded-full blur-2xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-teal-200/30 rounded-full blur-2xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-purple-200/30 rounded-full blur-xl animate-pulse delay-500"></div>
          <div className="absolute top-1/4 right-1/3 w-36 h-36 bg-cyan-200/30 rounded-full blur-2xl animate-pulse delay-1500"></div>

          {/* Additional animated elements */}
          <div className="absolute top-16 right-16 opacity-10">
            <div className="w-12 h-12 bg-gray-200/50 rounded-lg flex items-center justify-center animate-bounce delay-300">
              <i className="ri-shopping-cart-line text-xl text-gray-400"></i>
            </div>
          </div>
          <div className="absolute bottom-16 left-16 opacity-10">
            <div className="w-10 h-10 bg-gray-200/50 rounded-lg flex items-center justify-center animate-bounce delay-700">
              <i className="ri-settings-4-line text-lg text-gray-400"></i>
            </div>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Featured Products
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              Premium commercial laundry equipment for your business - discover our most popular and trusted solutions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-product-shop>
            {products.map((product, index) => (
              <div key={index} className="group cursor-pointer">
                <div className="bg-white/40 backdrop-blur-2xl rounded-3xl shadow-2xl hover:shadow-3xl transition-all duration-500 group-hover:-translate-y-3 group-hover:scale-105 border border-white/30 relative overflow-hidden h-full">
                  {/* Glass Background Layers */}
                  <div className="absolute inset-0 bg-white/20 backdrop-blur-xl rounded-3xl"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-white/5 rounded-3xl"></div>
                  
                  {/* Floating Particles */}
                  <div className="absolute top-4 right-4 w-1 h-1 bg-blue-400/60 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-500"></div>
                  <div className="absolute bottom-6 left-6 w-1.5 h-1.5 bg-teal-400/50 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-all duration-700" style={{ animationDelay: '0.3s' }}></div>

                  <div className="relative z-10 h-full flex flex-col">
                    {/* Image Container */}
                    <div className="aspect-video relative overflow-hidden rounded-t-3xl">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      
                      {/* Capacity Badge */}
                      <div className="absolute top-4 left-4">
                        <div className="bg-white/90 backdrop-blur-2xl border border-white/40 text-blue-600 px-3 py-2 rounded-2xl text-sm font-bold shadow-2xl">
                          <i className="ri-stack-line mr-1"></i>
                          Featured
                        </div>
                      </div>

                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 rounded-t-3xl"></div>
                    </div>

                    {/* Content Container */}
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 border border-white/30 group-hover:bg-white/30 transition-all duration-500 h-full flex flex-col">
                        <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-blue-700 transition-colors duration-300">
                          {product.name}
                        </h3>
                        
                        <p className="text-gray-700 text-sm mb-4 flex-1 group-hover:text-gray-800 transition-colors duration-300">
                          {product.description}
                        </p>
                        
                        {/* Features */}
                        <div className="space-y-4">
                          <div className="flex flex-wrap gap-2">
                            <span className="bg-white/50 backdrop-blur-xl text-gray-700 px-3 py-1 rounded-xl text-xs border border-white/40 shadow-lg group-hover:bg-white/70 transition-all duration-300">
                              Premium Quality
                            </span>
                            <span className="bg-white/50 backdrop-blur-xl text-gray-700 px-3 py-1 rounded-xl text-xs border border-white/40 shadow-lg group-hover:bg-white/70 transition-all duration-300">
                              Energy Efficient
                            </span>
                          </div>
                          
                          {/* CTA */}
                          <div className="flex items-center justify-center pt-4 border-t border-white/20">
                            <Link to="/products">
                              <div className="group/btn relative">
                                <div className="absolute inset-0 bg-blue-700/80 backdrop-blur-xl rounded-xl transform translate-x-1 translate-y-1 group-hover:translate-x-2 group-hover:translate-y-2 transition-transform duration-500 shadow-xl"></div>
                                <button className="relative bg-blue-600/90 backdrop-blur-2xl hover:bg-blue-700/90 text-white font-bold px-4 py-2 rounded-xl transition-all duration-500 transform hover:-translate-y-1 hover:scale-105 shadow-xl hover:shadow-2xl whitespace-nowrap border border-white/20 text-sm">
                                  <span className="flex items-center">
                                    <div className="w-4 h-4 bg-white/20 backdrop-blur-xl rounded flex items-center justify-center mr-2 group-hover/btn:scale-110 transition-transform duration-300">
                                      <i className="ri-eye-line text-xs"></i>
                                    </div>
                                    Learn More
                                  </span>
                                </button>
                              </div>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Hover Arrow Indicator */}
                    <div className="absolute bottom-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500 border border-white/40">
                      <i className="ri-arrow-up-right-line text-blue-600 text-lg group-hover:scale-110 transition-transform duration-300"></i>
                    </div>
                  </div>

                  {/* Subtle Shine Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-3xl"></div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 border border-white/20 relative overflow-hidden shadow-2xl max-w-2xl mx-auto">
              {/* Card Background Elements */}
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-200/30 to-blue-300/20 rounded-full blur-xl"></div>
              <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-teal-200/20 to-transparent rounded-full"></div>

              <div className="relative z-10">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Explore Our Complete Range
                </h3>
                <p className="text-gray-700 mb-6 text-lg">
                  Discover hundreds of premium laundry equipment options designed for every business need
                </p>

                <Link to="/products">
                  <Button variant="primary" size="lg" className="shadow-lg hover:shadow-xl">
                    View All Products
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-teal-50">
        {/* Beautiful Background */}
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
            style={{
              backgroundImage: `url('/images/backgrounds/industries-bg.jpg')`,
            }}
          />

          {/* Floating Elements */}
          <div className="absolute top-20 left-20 w-32 h-32 bg-blue-200/30 rounded-full blur-2xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-teal-200/30 rounded-full blur-2xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-purple-200/30 rounded-full blur-xl animate-pulse delay-500"></div>
          <div className="absolute top-1/4 right-1/3 w-36 h-36 bg-cyan-200/30 rounded-full blur-2xl animate-pulse delay-1500"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Industries We Serve
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              Specialized laundry equipment and services designed to meet the unique requirements of different business sectors
            </p>
          </div>

          {/* 1x4 Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {/* Hotels & Resorts */}
            <div className="group relative">
              <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-6 shadow-2xl hover:shadow-3xl transition-all duration-500 group-hover:-translate-y-2 border border-white/20 relative overflow-hidden h-full">
                {/* Card Background Elements */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-400/20 to-blue-600/10 rounded-full blur-xl"></div>
                <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-blue-500/10 to-transparent rounded-full"></div>

                <div className="relative z-10">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <i className="ri-hotel-line text-white text-xl"></i>
                  </div>

                  <h3 className="text-xl font-bold text-gray-800 mb-3">
                    Hotels & Resorts
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                    Complete laundry solutions for hospitality industry with high-volume requirements
                  </p>

                  <div className="grid grid-cols-1 gap-2 mb-6">
                    <div className="flex items-center text-xs">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                      <span className="text-gray-700">24/7 Operation</span>
                    </div>
                    <div className="flex items-center text-xs">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                      <span className="text-gray-700">Energy Efficient</span>
                    </div>
                    <div className="flex items-center text-xs">
                      <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                      <span className="text-gray-700">Guest Satisfaction</span>
                    </div>
                  </div>

                  <button className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-300 transform group-hover:-translate-y-1 shadow-lg hover:shadow-xl text-sm whitespace-nowrap">
                    HOSPITALITY SOLUTIONS <i className="ri-arrow-right-line ml-2"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Hospitals & Healthcare */}
            <div className="group relative">
              <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-6 shadow-2xl hover:shadow-3xl transition-all duration-500 group-hover:-translate-y-2 border border-white/20 relative overflow-hidden h-full">
                {/* Card Background Elements */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-red-400/20 to-red-600/10 rounded-full blur-xl"></div>
                <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-red-500/10 to-transparent rounded-full"></div>

                <div className="relative z-10">
                  <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <i className="ri-hospital-line text-white text-xl"></i>
                  </div>

                  <h3 className="text-xl font-bold text-gray-800 mb-3">
                    Hospitals & Healthcare
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                    Hygienic washing systems meeting medical industry standards and regulations
                  </p>

                  <div className="grid grid-cols-1 gap-2 mb-6">
                    <div className="flex items-center text-xs">
                      <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                      <span className="text-gray-700">Medical Grade</span>
                    </div>
                    <div className="flex items-center text-xs">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                      <span className="text-gray-700">Infection Control</span>
                    </div>
                    <div className="flex items-center text-xs">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                      <span className="text-gray-700">Compliance Ready</span>
                    </div>
                  </div>

                  <button className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-300 transform group-hover:-translate-y-1 shadow-lg hover:shadow-xl text-sm whitespace-nowrap">
                    HEALTHCARE SOLUTIONS <i className="ri-arrow-right-line ml-2"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Laundromats */}
            <div className="group relative">
              <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-6 shadow-2xl hover:shadow-3xl transition-all duration-500 group-hover:-translate-y-2 border border-white/20 relative overflow-hidden h-full">
                {/* Card Background Elements */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-teal-400/20 to-teal-600/10 rounded-full blur-xl"></div>
                <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-teal-500/10 to-transparent rounded-full"></div>

                <div className="relative z-10">
                  <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <i className="ri-building-2-line text-white text-xl"></i>
                  </div>

                  <h3 className="text-xl font-bold text-gray-800 mb-3">
                    Laundromats
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                    Self-service and commercial laundry equipment for retail laundry businesses
                  </p>

                  <div className="grid grid-cols-1 gap-2 mb-6">
                    <div className="flex items-center text-xs">
                      <div className="w-2 h-2 bg-teal-500 rounded-full mr-2"></div>
                      <span className="text-gray-700">User Friendly</span>
                    </div>
                    <div className="flex items-center text-xs">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></div>
                      <span className="text-gray-700">Coin/Card Ready</span>
                    </div>
                    <div className="flex items-center text-xs">
                      <div className="w-2 h-2 bg-purple-5 0 rounded-full mr-2"></div>
                      <span className="text-gray-700">Reliable Operations</span>
                    </div>
                  </div>

                  <button className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-300 transform group-hover:-translate-y-1 shadow-lg hover:shadow-xl text-sm whitespace-nowrap">
                    LAUNDROMAT SOLUTIONS <i className="ri-arrow-right-line ml-2"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Industrial Facilities */}
            <div className="group relative">
              <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-6 shadow-2xl hover:shadow-3xl transition-all duration-500 group-hover:-translate-y-2 border border-white/20 relative overflow-hidden h-full">
                {/* Card Background Elements */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-indigo-400/20 to-indigo-600/10 rounded-full blur-xl"></div>
                <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-indigo-500/10 to-transparent rounded-full"></div>

                <div className="relative z-10">
                  <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <i className="ri-factory-line text-white text-xl"></i>
                  </div>

                  <h3 className="text-xl font-bold text-gray-800 mb-3">
                    Industrial Facilities
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                    Heavy‑duty equipment for manufacturing, textile and industrial applications
                  </p>

                  <div className="grid grid-cols-1 gap-2 mb-6">
                    <div className="flex items-center text-xs">
                      <div className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></div>
                      <span className="text-gray-700">Heavy Duty</span>
                    </div>
                    <div className="flex items-center text-xs">
                      <div className="w-2 h-2 bg-gray-500 rounded-full mr-2"></div>
                      <span className="text-gray-700">High Capacity</span>
                    </div>
                    <div className="flex items-center text-xs">
                      <div className="w-2 h-2 bg-slate-500 rounded-full mr-2"></div>
                      <span className="text-gray-700">Continuous Operation</span>
                    </div>
                  </div>

                  <button className="w-full bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-300 transform group-hover:-translate-y-1 shadow-lg hover:shadow-xl text-sm whitespace-nowrap">
                    INDUSTRIAL SOLUTIONS <i className="ri-arrow-right-line ml-2"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Additional CTA Section */}
          <div className="mt-16 text-center">
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 relative overflow-hidden">
              <div className="relative z-10">
                <h3 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900">
                  Join 500+ Satisfied Customers
                </h3>
                <p className="text-gray-700 mb-6 max-w-2xl mx-auto text-lg">
                  Experience the difference with our premium laundry equipment and exceptional service standards.
                </p>

                <div className="flex flex-wrap justify-center items-center gap-8 mb-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">4.9/5</div>
                    <div className="text-sm text-gray-600">Customer Rating</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-teal-600">500+</div>
                    <div className="text-sm text-gray-600">Happy Customers</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-600">24/7</div>
                    <div className="text-sm text-gray-600">Support Available</div>
                  </div>
                </div>

                <button className="bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-800 hover:text-gray-900 font-bold px-8 py-3 rounded-xl transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl whitespace-nowrap">
                  SEE MORE TESTIMONIALS <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Testimonials */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-gray-50 via-blue-50 to-white">
        {/* Background */}
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-5"
            style={{
              backgroundImage: `url('/images/backgrounds/testimonials-bg.jpg')`,
            }}
          />

          {/* Floating Elements */}
          <div className="absolute top-16 left-16 w-24 h-24 bg-blue-100/50 rounded-full blur-2xl animate-pulse"></div>
          <div className="absolute bottom-16 right-16 w-32 h-32 bg-teal-100/50 rounded-full blur-2xl animate-pulse"></div>
          <div className="absolute top-1/3 right-1/4 w-20 h-20 bg-purple-100/50 rounded-full blur-xl animate-pulse"></div>
          <div className="absolute bottom-1/3 left-1/4 w-28 h-28 bg-cyan-100/50 rounded-full blur-2xl animate-pulse"></div>

          {/* Additional animated elements */}
          <div className="absolute top-20 right-20 opacity-10">
            <div className="w-16 h-16 bg-gray-200/50 rounded-lg flex items-center justify-center animate-bounce">
              <i className="ri-chat-quote-line text-2xl text-gray-400"></i>
            </div>
          </div>
          <div className="absolute bottom-20 left-20 opacity-10">
            <div className="w-14 h-14 bg-gray-200/50 rounded-lg flex items-center justify-center animate-bounce">
              <i className="ri-star-line text-xl text-gray-400"></i>
            </div>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What Our Customers Say
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              Real experiences from businesses across India who trust our laundry solutions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="group relative">
              <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-500 group-hover:-translate-y-3 border border-white/20 relative overflow-hidden h-full">
                {/* Card Background Elements */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-400/20 to-blue-600/10 rounded-full blur-xl"></div>
                <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-blue-500/10 rounded-full"></div>

                <div className="relative z-10">
                  {/* Rating Stars */}
                  <div className="flex justify-center mb-6">
                    {[...Array(testimonials[0].rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-400 text-2xl mx-1"></i>
                    ))}
                  </div>

                  {/* Quote */}
                  <div className="mb-6 relative text-center">
                    <i className="ri-double-quotes-l text-4xl text-blue-500/30 absolute left-0 top-0"></i>
                    <p className="text-gray-700 text-lg leading-relaxed italic pl-6 pr-6">{testimonials[0].content}</p>
                    <i className="ri-double-quotes-r text-4xl text-blue-500/30 absolute right-0 bottom-0"></i>
                  </div>

                  {/* Customer Info */}
                  <div className="flex items-center justify-center">
                    <div className="relative">
                      <img
                        src={testimonials[0].image}
                        alt={testimonials[0].name}
                        className="w-16 h-16 rounded-full object-cover border-4 border-white shadow-lg"
                      />
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                        <i className="ri-hotel-line text-white text-xs"></i>
                      </div>
                    </div>
                    <div className="ml-4 text-center">
                      <h4 className="font-bold text-gray-800 text-lg">{testimonials[0].name}</h4>
                      <p className="text-sm text-gray-600 font-medium">{testimonials[0].position}</p>
                      <p className="text-sm text-blue-600 font-semibold">{testimonials[0].company}</p>
                    </div>
                  </div>

                  {/* Industry Badge */}
                  <div className="mt-6 flex justify-center">
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                      <i className="ri-hotel-line mr-1"></i> Hospitality Industry
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="group relative">
              <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-500 group-hover:-translate-y-3 border border-white/20 relative overflow-hidden h-full">
                {/* Card background */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-red-400/20 to-red-600/10 rounded-full blur-xl"></div>
                <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-red-500/10 rounded-full"></div>

                <div className="relative z-10">
                  <div className="flex justify-center mb-6">
                    {[...Array(testimonials[1].rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-400 text-2xl mx-1"></i>
                    ))}
                  </div>

                  <div className="mb-6 relative text-center">
                    <i className="ri-double-quotes-l text-4xl text-red-500/30 absolute left-0 top-0"></i>
                    <p className="text-gray-700 text-lg leading-relaxed italic pl-6 pr-6">{testimonials[1].content}</p>
                    <i className="ri-double-quotes-r text-4xl text-red-500/30 absolute right-0 bottom-0"></i>
                  </div>

                  <div className="flex items-center justify-center">
                    <div className="relative">
                      <img
                        src={testimonials[1].image}
                        alt={testimonials[1].name}
                        className="w-16 h-16 rounded-full object-cover border-4 border-white shadow-lg"
                      />
                    </div>
                    <div className="ml-4 text-center">
                      <h4 className="font-bold text-gray-800 text-lg">{testimonials[1].name}</h4>
                      <p className="text-sm text-gray-600 font-medium">{testimonials[1].position}</p>
                      <p className="text-sm text-blue-600 font-semibold">{testimonials[1].company}</p>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-center">
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium">
                      <i className="ri-hospital-line mr-1"></i> Healthcare Industry
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="group relative">
              <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-500 group-hover:-translate-y-3 border border-white/20 relative overflow-hidden h-full">
                {/* Card background */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-green-400/20 to-green-600/10 rounded-full blur-xl"></div>
                <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-green-500/10 rounded-full"></div>

                <div className="relative z-10">
                  <div className="flex justify-center mb-6">
                    {[...Array(testimonials[2].rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-400 text-2xl mx-1"></i>
                    ))}
                  </div>

                  <div className="mb-6 relative text-center">
                    <i className="ri-double-quotes-l text-4xl text-green-500/30 absolute left-0 top-0"></i>
                    <p className="text-gray-700 text-lg leading-relaxed italic pl-6 pr-6">{testimonials[2].content}</p>
                    <i className="ri-double-quotes-r text-4xl text-green-500/30 absolute right-0 bottom-0"></i>
                  </div>

                  <div className="flex items-center justify-center">
                    <div className="relative">
                      <img
                        src={testimonials[2].image}
                        alt={testimonials[2].name}
                        className="w-16 h-16 rounded-full object-cover border-4 border-white shadow-lg"
                      />
                    </div>
                    <div className="ml-4 text-center">
                      <h4 className="font-bold text-gray-800 text-lg">{testimonials[2].name}</h4>
                      <p className="text-sm text-gray-600 font-medium">{testimonials[2].position}</p>
                      <p className="text-sm text-blue-600 font-semibold">{testimonials[2].company}</p>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-center">
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                      <i className="ri-building-2-line mr-1"></i> Laundromat Industry
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-teal-50">
        {/* Placeholder for a final CTA – can be customized as needed */}
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-800 mb-6">
            Ready to Upgrade Your Laundry Operations?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Contact our experts today and get a tailored solution that fits your business needs.
          </p>
          <Link to="/contact">
            <Button variant="primary" size="lg">
              Get a Free Consultation <i className="ri-phone-line ml-2"></i>
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
